import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import axios from 'axios';
import { resellerAPI, applicationsAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Plus, Edit, Trash2, Users, Target, Gift, Trophy, FileText, CheckCircle, XCircle, Crown, Bell, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import ResellerSalesHistory from './ResellerSalesHistory';
import VisibilityManagerModal from './VisibilityManagerModal';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import ResellerNoticesManagement from './ResellerNoticesManagement';


// Sub-componente: Card sortable de tarefa de revendedor
const SortableTaskCard = ({
  task,
  idx,
  total,
  isEditingPosition,
  positionInput,
  onStartEditPosition,
  onChangePositionInput,
  onCommitPosition,
  onCancelEditPosition,
  onMove,
  onToggleActive,
  onDelete,
  onEdit,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <Card ref={setNodeRef} style={style} data-testid={`task-card-${task.id}`} className={isDragging ? 'shadow-2xl ring-2 ring-blue-400' : ''}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* Drag handle */}
            <button
              {...attributes}
              {...listeners}
              className="touch-none cursor-grab active:cursor-grabbing p-1 -ml-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
              title="Arraste para reordenar"
              data-testid={`task-drag-handle-${task.id}`}
              aria-label="Arrastar"
            >
              <GripVertical className="h-5 w-5" />
            </button>

            {/* Editable position badge */}
            {isEditingPosition ? (
              <Input
                type="number"
                min={1}
                max={total}
                value={positionInput}
                onChange={(e) => onChangePositionInput(e.target.value)}
                onBlur={onCommitPosition}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onCommitPosition();
                  if (e.key === 'Escape') onCancelEditPosition();
                }}
                autoFocus
                className="h-7 w-14 text-center font-mono text-sm"
                data-testid={`task-position-input-${task.id}`}
              />
            ) : (
              <button
                onClick={onStartEditPosition}
                className="font-mono text-xs px-2 py-0.5 rounded border border-gray-200 bg-gray-50 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                title="Clique para mover para uma posição específica"
                data-testid={`task-position-badge-${task.id}`}
              >
                #{idx + 1}
              </button>
            )}

            <CardTitle className="text-lg truncate">{task.title}</CardTitle>
          </div>
          <Badge variant={task.active ? 'default' : 'secondary'}>
            {task.active ? 'Ativa' : 'Inativa'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600">{task.description}</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500">Meta de Vendas</p>
            <p className="text-lg font-bold text-purple-600">{task.target_sales_coins.toLocaleString()} 🪙</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Bônus</p>
            <p className="text-lg font-bold text-green-600">{task.reward_coins.toLocaleString()} 🎁</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            disabled={idx === 0}
            onClick={() => onMove(task.id, -1)}
            data-testid={`task-move-up-${task.id}`}
            title="Mover para cima"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            disabled={idx === total - 1}
            onClick={() => onMove(task.id, +1)}
            data-testid={`task-move-down-${task.id}`}
            title="Mover para baixo"
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant={task.active ? 'outline' : 'default'}
            onClick={() => onToggleActive(task.id, !task.active)}
            className="flex-1"
          >
            {task.active ? '🔴 Desativar' : '🟢 Ativar'}
          </Button>
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Edit className="mr-1 h-3 w-3" />
            Editar
          </Button>
          <Button size="sm" variant="destructive" onClick={() => onDelete(task.id)}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


// Sub-componente para Histórico de Ganhadores
const WinnersHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [selectedWinners, setSelectedWinners] = useState([]); // NOVO

  useEffect(() => {
    loadHistory();
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/config/`);
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config:', error);
    }
  };

  const toggleWinnersBanner = async (active) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      await axios.put(`${BACKEND_URL}/api/config/?show_winners_banner=${active}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Banner de vencedores ${active ? 'ativado' : 'desativado'}!`);
      loadConfig();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const loadHistory = async () => {
    try {
      const response = await applicationsAPI.getWinnersHistory(8); // Últimas 8 semanas
      setHistory(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      toast.error('Erro ao carregar ganhadores');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedWinners.length === 0) {
      toast.error('Selecione ao menos um ganhador');
      return;
    }

    if (!window.confirm(`Excluir ${selectedWinners.length} ganhador(es)?`)) {
      return;
    }

    try {
      await applicationsAPI.deleteWinnersBulk(selectedWinners);
      toast.success(`${selectedWinners.length} ganhador(es) excluído(s)!`);
      setSelectedWinners([]);
      loadHistory();
    } catch (error) {
      toast.error('Erro ao excluir ganhadores');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><FoxSpinnerInline size="lg" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Controle do Banner */}
      {config && (
        <Card className="bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Crown className="h-5 w-5 text-yellow-500" />
                <Label>Banner TOP 3 na Home</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={config.show_winners_banner || false}
                  onCheckedChange={toggleWinnersBanner}
                />
                <Badge variant={config.show_winners_banner ? 'default' : 'secondary'}>
                  {config.show_winners_banner ? 'ATIVO' : 'INATIVO'}
                </Badge>
              </div>
            </div>
            {config.show_winners_banner && (
              <p className="text-xs text-gray-600 mt-2">
                ✓ Banner TOP 3 será exibido na página inicial para todos os visitantes
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown className="h-6 w-6 text-yellow-500" />
          <h3 className="text-xl font-bold">Histórico de Ganhadores Semanais</h3>
        </div>
        {selectedWinners.length > 0 && (
          <div className="flex items-center gap-2">
            <Badge variant="destructive">{selectedWinners.length} selecionado(s)</Badge>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDeleteSelected}
            >
              <Trash2 className="mr-1 h-4 w-4" />
              Excluir Selecionados
            </Button>
          </div>
        )}
      </div>

      {history.length === 0 ? (
        <p className="text-center text-gray-500 py-8">Nenhuma semana finalizada ainda</p>
      ) : (
        history.map((week) => (
          <Card key={week.week_start}>
            <CardHeader>
              <CardTitle className="text-lg">
                📅 Semana de {new Date(week.week_start).toLocaleDateString('pt-BR')} a {new Date(week.week_end).toLocaleDateString('pt-BR')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* TOP AGENTES */}
                <div>
                  <h4 className="font-bold mb-3 text-purple-900">💼 TOP 10 AGENTES</h4>
                  <div className="space-y-2">
                    {week.agentes.length === 0 ? (
                      <p className="text-sm text-gray-500">Nenhum agente</p>
                    ) : (
                      week.agentes.map((w) => (
                        <div key={w.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                          <Checkbox
                            checked={selectedWinners.includes(w.id)}
                            onCheckedChange={() => {
                              if (selectedWinners.includes(w.id)) {
                                setSelectedWinners(selectedWinners.filter(id => id !== w.id));
                              } else {
                                setSelectedWinners([...selectedWinners, w.id]);
                              }
                            }}
                          />
                          <span className="font-bold text-lg w-6">{w.position}º</span>
                          {w.kako_avatar && <img src={w.kako_avatar} alt="" className="w-8 h-8 rounded-full" />}
                          <div className="flex-1">
                            <div className="text-sm font-medium">{w.nickname}</div>
                            <div className="text-xs text-gray-600">{w.week_coins.toLocaleString()} 🪙</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* TOP HOSTS */}
                <div>
                  <h4 className="font-bold mb-3 text-purple-900">🎭 TOP 10 HOSTS</h4>
                  <div className="space-y-2">
                    {week.hosts.length === 0 ? (
                      <p className="text-sm text-gray-500">Nenhum host</p>
                    ) : (
                      week.hosts.map((w) => (
                        <div key={w.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                          <Checkbox
                            checked={selectedWinners.includes(w.id)}
                            onCheckedChange={() => {
                              if (selectedWinners.includes(w.id)) {
                                setSelectedWinners(selectedWinners.filter(id => id !== w.id));
                              } else {
                                setSelectedWinners([...selectedWinners, w.id]);
                              }
                            }}
                          />
                          <span className="font-bold text-lg w-6">{w.position}º</span>
                          {w.kako_avatar && <img src={w.kako_avatar} alt="" className="w-8 h-8 rounded-full" />}
                          <div className="flex-1">
                            <div className="text-sm font-medium">{w.nickname}</div>
                            <div className="text-xs text-gray-600">{w.week_coins.toLocaleString()} 🪙</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

// Sub-componente para Abas de Ranking
const RankingTabs = () => {
  const [rankingType, setRankingType] = useState('weekly'); // 'weekly' ou 'general'
  const [typeFilter, setTypeFilter] = useState('AGENTE'); // Padrão: AGENTE primeiro
  const [weeklyRanking, setWeeklyRanking] = useState([]);
  const [generalRanking, setGeneralRanking] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    loadRankings();
  }, []);

  // Cronômetro - atualiza a cada segundo
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      
      // Calcula o fim da semana (Domingo 23:59:59)
      const endOfWeek = new Date(now);
      const daysUntilSunday = 7 - now.getDay(); // 0 = Domingo, 6 = Sábado
      endOfWeek.setDate(now.getDate() + (daysUntilSunday === 7 ? 0 : daysUntilSunday));
      endOfWeek.setHours(23, 59, 59, 999);
      
      const diff = endOfWeek.getTime() - now.getTime();
      
      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        setTimeRemaining({ days, hours, minutes, seconds });
      } else {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const loadRankings = async () => {
    setLoading(true);
    try {
      const [weeklyRes, generalRes] = await Promise.all([
        resellerAPI.getRankingWeekly(),
        resellerAPI.getRankingAdmin()
      ]);
      setWeeklyRanking(weeklyRes.data);
      setGeneralRanking(generalRes.data);
    } catch (error) {
      console.error('Erro ao carregar rankings:', error);
      toast.error('Erro ao carregar rankings');
    } finally {
      setLoading(false);
    }
  };

  const renderRankingList = (ranking, isWeekly = false) => {
    if (ranking.length === 0) {
      return (
        <p className="text-center text-gray-500 py-8">
          {isWeekly ? 'Nenhuma venda esta semana' : 'Nenhum afiliado ainda'}
        </p>
      );
    }

    return ranking.map((reseller) => (
      <Card 
        key={reseller.id}
        className={`${
          reseller.position === 1 ? 'border-yellow-400 border-2' :
          reseller.position === 2 ? 'border-gray-400 border-2' :
          reseller.position === 3 ? 'border-orange-400 border-2' : ''
        }`}
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="text-center min-w-[50px]">
              <div className="text-2xl font-bold">
                {reseller.position === 1 && '🥇'}
                {reseller.position === 2 && '🥈'}
                {reseller.position === 3 && '🥉'}
                {reseller.position > 3 && `${reseller.position}º`}
              </div>
            </div>
            {reseller.kako_avatar && (
              <img
                src={reseller.kako_avatar}
                alt={reseller.nickname}
                className="w-12 h-12 rounded-full border-2 border-white shadow"
              />
            )}
            <div className="flex-1">
              <p className="font-bold text-lg">{reseller.nickname}</p>
              <p className="text-sm text-gray-600">ID: {reseller.kako_id}</p>
              {isWeekly && reseller.week_start && (
                <p className="text-xs text-gray-500">
                  {new Date(reseller.week_start).toLocaleDateString('pt-BR')} - {new Date(reseller.week_end).toLocaleDateString('pt-BR')}
                </p>
              )}
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-purple-600">
                {isWeekly 
                  ? reseller.week_coins?.toLocaleString() 
                  : reseller.total_sales_coins?.toLocaleString()
                } 🪙
              </div>
              <p className="text-xs text-gray-600">
                {isWeekly ? 'Vendas da Semana' : 'Total de Vendas'}
              </p>
              {isWeekly && (
                <p className="text-xs text-gray-500">
                  {reseller.week_orders} pedido(s)
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    ));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <FoxSpinnerInline size="md" />
      </div>
    );
  }

  return (
    <Tabs value={rankingType} onValueChange={(v) => { setRankingType(v); setTypeFilter('AGENTE'); }}>
      <div className="overflow-x-auto">
        <TabsList className="inline-flex w-auto min-w-full bg-gray-100">
          <TabsTrigger value="weekly" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=inactive]:bg-purple-100 data-[state=inactive]:text-purple-600">
            📅 Ranking Semanal
          </TabsTrigger>
          <TabsTrigger value="general" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white data-[state=inactive]:bg-orange-100 data-[state=inactive]:text-orange-600">
            🏆 Ranking Geral
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="weekly" className="mt-4 space-y-4">
        {/* Cronômetro */}
        <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-sm mb-2">⏰ Tempo restante da semana</p>
              <div className="flex justify-center gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold">{timeRemaining.days}</div>
                  <div className="text-xs">Dias</div>
                </div>
                <div className="text-3xl font-bold">:</div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{String(timeRemaining.hours).padStart(2, '0')}</div>
                  <div className="text-xs">Horas</div>
                </div>
                <div className="text-3xl font-bold">:</div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{String(timeRemaining.minutes).padStart(2, '0')}</div>
                  <div className="text-xs">Minutos</div>
                </div>
                <div className="text-3xl font-bold">:</div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{String(timeRemaining.seconds).padStart(2, '0')}</div>
                  <div className="text-xs">Segundos</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Sub-abas de filtro por tipo - AGENTE primeiro */}
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <div className="overflow-x-auto">
            <TabsList className="grid w-full grid-cols-2 bg-gray-100">
              <TabsTrigger value="AGENTE" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=inactive]:bg-purple-100 data-[state=inactive]:text-purple-600">
                💼 AGENTE ({weeklyRanking.filter(r => r.type === 'AGENTE').length})
              </TabsTrigger>
              <TabsTrigger value="HOST" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white data-[state=inactive]:bg-orange-100 data-[state=inactive]:text-orange-600">
                🎭 HOST ({weeklyRanking.filter(r => r.type === 'HOST').length})
              </TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="AGENTE" className="mt-3">
            <div className="space-y-3">
              {renderRankingList(weeklyRanking.filter(r => r.type === 'AGENTE'), true)}
            </div>
          </TabsContent>
          
          <TabsContent value="HOST" className="mt-3">
            <div className="space-y-3">
              {renderRankingList(weeklyRanking.filter(r => r.type === 'HOST'), true)}
            </div>
          </TabsContent>
        </Tabs>
      </TabsContent>

      <TabsContent value="general" className="mt-4 space-y-4">
        {/* Sub-abas de filtro por tipo - AGENTE primeiro */}
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <div className="overflow-x-auto">
            <TabsList className="grid w-full grid-cols-2 bg-gray-100">
              <TabsTrigger value="AGENTE" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=inactive]:bg-purple-100 data-[state=inactive]:text-purple-600">
                💼 AGENTE ({generalRanking.filter(r => r.type === 'AGENTE').length})
              </TabsTrigger>
              <TabsTrigger value="HOST" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white data-[state=inactive]:bg-orange-100 data-[state=inactive]:text-orange-600">
                🎭 HOST ({generalRanking.filter(r => r.type === 'HOST').length})
              </TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="AGENTE" className="mt-3">
            <div className="space-y-3">
              {renderRankingList(generalRanking.filter(r => r.type === 'AGENTE'), false)}
            </div>
          </TabsContent>
          
          <TabsContent value="HOST" className="mt-3">
            <div className="space-y-3">
              {renderRankingList(generalRanking.filter(r => r.type === 'HOST'), false)}
            </div>
          </TabsContent>
        </Tabs>
      </TabsContent>
    </Tabs>
  );
};

// Sub-componente para Aba de Inscrições
const ApplicationsTabs = ({ applications, onApprove, onReject, onReload }) => {
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'HOST', 'AGENTE'
  const [config, setConfig] = useState(null);
  const [selectedApps, setSelectedApps] = useState([]); // Novo
  const [searchKakoId, setSearchKakoId] = useState(''); // NOVO

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/config/`);
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config:', error);
    }
  };

  const toggleApplications = async (type, active) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      const param = type === 'HOST' ? 'applications_host_active' : 'applications_agente_active';
      await axios.put(`${BACKEND_URL}/api/config/?${param}=${active}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Inscrições ${type} ${active ? 'ativadas' : 'desativadas'}!`);
      loadConfig();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedApps.length === 0) {
      toast.error('Selecione ao menos uma solicitação');
      return;
    }

    if (!window.confirm(`Excluir ${selectedApps.length} solicitação(ões)? O usuário poderá se inscrever novamente.`)) {
      return;
    }

    try {
      await applicationsAPI.deleteBulk(selectedApps);
      toast.success(`${selectedApps.length} solicitação(ões) excluída(s)!`);
      setSelectedApps([]);
      
      // Atualiza apenas as applications, sem recarregar a página inteira
      if (typeof onReload === 'function') {
        onReload();
      }
    } catch (error) {
      toast.error('Erro ao excluir solicitações');
    }
  };

  const toggleSelectAll = () => {
    if (selectedApps.length === filteredApps.length) {
      setSelectedApps([]);
    } else {
      setSelectedApps(filteredApps.map(app => app.id));
    }
  };

  // Auto-muda de aba quando encontra solicitação específica
  useEffect(() => {
    if (searchKakoId.trim() === '') return;
    
    // Busca em TODAS as applications (sem filtro de aba)
    const found = applications.filter(app => app.kako_id.includes(searchKakoId));
    
    if (found.length === 1) {
      const app = found[0];
      
      // Muda para aba correta automaticamente
      if (app.status === 'rejected') {
        setTypeFilter('rejected');
      } else if (app.type === 'HOST') {
        setTypeFilter('HOST');
      } else if (app.type === 'AGENTE') {
        setTypeFilter('AGENTE');
      }
    }
  }, [searchKakoId, applications]);

  const filteredApps = (typeFilter === 'all' 
    ? applications.filter(app => app.status === 'pending')
    : typeFilter === 'rejected'
    ? applications.filter(app => app.status === 'rejected')
    : applications.filter(app => app.type === typeFilter && app.status === 'pending'))
    .filter(app => searchKakoId === '' || app.kako_id.includes(searchKakoId)); // Filtro por KAKO ID

  return (
    <div className="space-y-4">
      {/* Controles de Inscrições */}
      {config && (
        <Card className="bg-blue-50">
          <CardContent className="p-4">
            <h4 className="font-bold mb-3">Controle de Inscrições</h4>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex items-center gap-3 flex-1">
                <Switch
                  checked={config.applications_host_active || false}
                  onCheckedChange={(c) => toggleApplications('HOST', c)}
                />
                <Label>Inscrições HOST</Label>
                <Badge variant={config.applications_host_active ? 'default' : 'secondary'}>
                  {config.applications_host_active ? 'ATIVAS' : 'INATIVAS'}
                </Badge>
              </div>
              <div className="flex items-center gap-3 flex-1">
                <Switch
                  checked={config.applications_agente_active || false}
                  onCheckedChange={(c) => toggleApplications('AGENTE', c)}
                />
                <Label>Inscrições AGENTE</Label>
                <Badge variant={config.applications_agente_active ? 'default' : 'secondary'}>
                  {config.applications_agente_active ? 'ATIVAS' : 'INATIVAS'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtro por tipo */}
      <div className="overflow-x-auto">
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <TabsList className="inline-flex w-auto min-w-full">
            <TabsTrigger value="all">
              Todas ({applications.filter(a => a.status === 'pending').length})
            </TabsTrigger>
            <TabsTrigger value="HOST">
              HOST ({applications.filter(a => a.type === 'HOST' && a.status === 'pending').length})
            </TabsTrigger>
            <TabsTrigger value="AGENTE">
              AGENTE ({applications.filter(a => a.type === 'AGENTE' && a.status === 'pending').length})
            </TabsTrigger>
            <TabsTrigger value="rejected">
              <XCircle className="w-4 h-4 mr-1" />
              RECUSADOS ({applications.filter(a => a.status === 'rejected').length})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Campo de busca por KAKO ID */}
      <div>
        <Label>Buscar por KAKO ID</Label>
        <Input
          type="text"
          placeholder="Digite o KAKO ID para filtrar..."
          value={searchKakoId}
          onChange={(e) => setSearchKakoId(e.target.value)}
          className="max-w-md"
        />
      </div>

      {/* Botões de ação em massa */}
      {filteredApps.length > 0 && (
        <div className="flex items-center gap-2 mb-4">
          <Button
            size="sm"
            variant="outline"
            onClick={toggleSelectAll}
          >
            {selectedApps.length === filteredApps.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
          </Button>
          {selectedApps.length > 0 && (
            <>
              <Badge variant="destructive">{selectedApps.length} selecionada(s)</Badge>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDeleteSelected}
              >
                <Trash2 className="mr-1 h-4 w-4" />
                Excluir Selecionadas
              </Button>
            </>
          )}
        </div>
      )}

      {/* Tabela de inscrições */}
      {filteredApps.length === 0 ? (
        <p className="text-center text-gray-500 py-8">Nenhuma inscrição pendente</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="p-3 text-center">
                      <Checkbox
                        checked={filteredApps.length > 0 && filteredApps.every(app => selectedApps.includes(app.id))}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="text-left p-3 font-semibold">Avatar</th>
                    <th className="text-left p-3 font-semibold">Nickname</th>
                    <th className="text-left p-3 font-semibold">KAKO ID</th>
                    <th className="text-center p-3 font-semibold">Tipo</th>
                    <th className="text-center p-3 font-semibold">Status</th>
                    <th className="text-left p-3 font-semibold">Dados Específicos</th>
                    <th className="text-left p-3 font-semibold">Data</th>
                    <th className="text-center p-3 font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map((app) => (
                    <tr key={app.id} className="border-b hover:bg-yellow-50">
                      {/* Checkbox */}
                      <td className="p-3 text-center">
                        <Checkbox
                          checked={selectedApps.includes(app.id)}
                          onCheckedChange={() => {
                            if (selectedApps.includes(app.id)) {
                              setSelectedApps(selectedApps.filter(id => id !== app.id));
                            } else {
                              setSelectedApps([...selectedApps, app.id]);
                            }
                          }}
                        />
                      </td>
                      
                      {/* Avatar */}
                      <td className="p-3">
                        {app.kako_avatar ? (
                          <img src={app.kako_avatar} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">👤</div>
                        )}
                      </td>
                      
                      {/* Nickname */}
                      <td className="p-3 font-medium">{app.nickname}</td>
                      
                      {/* KAKO ID */}
                      <td className="p-3 font-mono text-xs">{app.kako_id}</td>
                      
                      {/* Tipo */}
                      <td className="p-3 text-center">
                        <Badge variant={app.type === 'HOST' ? 'default' : 'secondary'}>
                          {app.type}
                        </Badge>
                      </td>
                      
                      {/* Status */}
                      <td className="p-3 text-center">
                        {app.status === 'pending' && (
                          <Badge className="bg-yellow-500">Pendente</Badge>
                        )}
                        {app.status === 'rejected' && (
                          <Badge variant="destructive">Rejeitado</Badge>
                        )}
                      </td>
                      
                      {/* Dados Específicos */}
                      <td className="p-3 text-xs text-gray-600">
                        {app.whatsapp && (
                          <div className="mb-1">📱 {app.whatsapp}</div>
                        )}
                        {app.type === 'HOST' && (
                          <div className="space-y-1">
                            <div>👥 {app.followers_count?.toLocaleString()} seguidores</div>
                            <div>🏢 {app.agency}</div>
                          </div>
                        )}
                        {app.type === 'AGENTE' && (
                          <div className="space-y-1">
                            <div>🏢 Agência: {app.agency_id}</div>
                            <div>👥 {app.host_count} hosts</div>
                          </div>
                        )}
                      </td>
                      
                      {/* Data */}
                      <td className="p-3 text-xs">
                        <div>{new Date(app.created_at).toLocaleDateString('pt-BR')}</div>
                        <div className="text-gray-500">{new Date(app.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      
                      {/* Ações */}
                      <td className="p-3">
                        {app.status === 'pending' ? (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              onClick={() => onApprove(app.id)}
                              className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                            >
                              <CheckCircle className="mr-1 h-3 w-3" />
                              Aprovar
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => onReject(app.id)}
                              className="h-7 text-xs"
                            >
                              <XCircle className="mr-1 h-3 w-3" />
                              Rejeitar
                            </Button>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500 italic">
                            Excluir para permitir nova solicitação
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Sub-componente para Aba de Revendedores (dividido por tipo)
const ResellersTabs = ({ resellers, onEdit, onDelete }) => {
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('nickname_asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [reportReseller, setReportReseller] = useState(null);
  const [reportMonth, setReportMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [generatingReport, setGeneratingReport] = useState(false);

  const generateReport = async () => {
    if (!reportReseller || !reportMonth) return;
    setGeneratingReport(true);
    try {
      const res = await resellerAPI.getSalesHistory({
        reseller_id: reportReseller.id,
        month: reportMonth,
        include_deleted: false,
        limit: 5000
      });
      const sales = res.data?.sales || [];
      const summary = res.data?.summary || { total_orders: 0, total_coins: 0, total_amount: 0 };

      const [yyyy, mm] = reportMonth.split('-');
      const periodoStr = `${mm}/${yyyy}`;

      // Geração de XLSX nativo com formatação
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      wb.creator = 'Moedas Online';
      wb.created = new Date();
      const ws = wb.addWorksheet('Relatório', {
        views: [{ state: 'frozen', ySplit: 8 }]
      });

      const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBFE3FF' } }; // azul claro
      const BOLD_FONT = { bold: true };
      const TITLE_FONT = { bold: true, size: 14 };

      // === Cabeçalho de informações ===
      ws.mergeCells('A1:G1');
      const titleCell = ws.getCell('A1');
      titleCell.value = 'Relatório de Vendas';
      titleCell.font = TITLE_FONT;
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = HEADER_FILL;
      ws.getRow(1).height = 24;

      const infoRows = [
        ['Revendedor', `${reportReseller.nickname || ''} (KAKO ${reportReseller.kako_id || ''})`],
        ['Total de moedas', summary.total_coins],
        ['Total de vendas', `${summary.total_orders} pedidos`],
        ['Período', periodoStr]
      ];
      infoRows.forEach((row, i) => {
        const r = i + 2;
        ws.getCell(`A${r}`).value = row[0];
        ws.getCell(`A${r}`).font = BOLD_FONT;
        ws.getCell(`B${r}`).value = row[1];
        if (row[0] === 'Total de moedas') {
          ws.getCell(`B${r}`).numFmt = '#,##0';
        }
      });

      // === Linha em branco ===
      // === Header da tabela (linha 7) ===
      const tableHeader = ['Data', 'Pedido', 'Comprador', 'KAKO ID Comprador', 'Revendedor', 'Moedas', 'Status'];
      const headerRow = ws.getRow(7);
      tableHeader.forEach((label, idx) => {
        const cell = headerRow.getCell(idx + 1);
        cell.value = label;
        cell.font = BOLD_FONT;
        cell.fill = HEADER_FILL;
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin' }, left: { style: 'thin' },
          bottom: { style: 'thin' }, right: { style: 'thin' }
        };
      });
      headerRow.height = 20;

      // === Linhas de dados ===
      sales.forEach((s, idx) => {
        const row = ws.getRow(8 + idx);
        row.getCell(1).value = s.completed_at ? new Date(s.completed_at).toLocaleString('pt-BR') : '-';
        row.getCell(2).value = s.order_id ? s.order_id.slice(0, 8) : '-';
        row.getCell(3).value = s.buyer_nickname || '-';
        row.getCell(4).value = s.buyer_kako_id || '-';
        row.getCell(5).value = s.reseller_nickname || reportReseller.nickname || '-';
        row.getCell(6).value = s.coins || 0;
        row.getCell(6).numFmt = '#,##0';
        row.getCell(7).value = 'Pago';
        row.getCell(7).alignment = { horizontal: 'center' };
      });

      // === Linha de total no final ===
      if (sales.length > 0) {
        const totalRow = ws.getRow(8 + sales.length);
        totalRow.getCell(5).value = 'TOTAL';
        totalRow.getCell(5).font = BOLD_FONT;
        totalRow.getCell(5).alignment = { horizontal: 'right' };
        totalRow.getCell(6).value = summary.total_coins;
        totalRow.getCell(6).font = BOLD_FONT;
        totalRow.getCell(6).numFmt = '#,##0';
        totalRow.getCell(6).fill = HEADER_FILL;
      }

      // === Larguras de coluna apropriadas ===
      ws.getColumn(1).width = 22; // Data
      ws.getColumn(2).width = 14; // Pedido
      ws.getColumn(3).width = 24; // Comprador (nickname)
      ws.getColumn(4).width = 18; // KAKO ID
      ws.getColumn(5).width = 28; // Revendedor
      ws.getColumn(6).width = 14; // Moedas
      ws.getColumn(7).width = 10; // Status

      // === Download ===
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio-${(reportReseller.nickname || 'afiliado').replace(/\s+/g, '_')}-${reportMonth}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Relatório gerado: ${sales.length} vendas`);
      setReportReseller(null);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao gerar relatório');
    } finally {
      setGeneratingReport(false);
    }
  };

  const filteredResellers = resellers.filter(r => {
    // Filtro por tipo
    if (typeFilter !== 'all' && r.type !== typeFilter) return false;
    // Filtro por busca (nome ou KAKO ID)
    const q = (searchTerm || '').trim().toLowerCase();
    if (!q) return true;
    return (
      (r.nickname || '').toLowerCase().includes(q) ||
      (r.kako_id || '').includes(q)
    );
  });
  
  // NOVO: Ordenação
  const sortedResellers = [...filteredResellers].sort((a, b) => {
    switch (sortBy) {
      case 'nickname_asc':
        return (a.nickname || '').localeCompare(b.nickname || '');
      case 'nickname_desc':
        return (b.nickname || '').localeCompare(a.nickname || '');
      case 'total_sales_desc':
        return (b.total_sales || 0) - (a.total_sales || 0);
      case 'total_sales_asc':
        return (a.total_sales || 0) - (b.total_sales || 0);
      case 'week_sales_desc':
        return (b.current_week_sales || 0) - (a.current_week_sales || 0);
      case 'week_sales_asc':
        return (a.current_week_sales || 0) - (b.current_week_sales || 0);
      case 'last_week_sales_desc':
        return (b.last_week_sales || 0) - (a.last_week_sales || 0);
      case 'last_week_sales_asc':
        return (a.last_week_sales || 0) - (b.last_week_sales || 0);
      case 'created_desc':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'created_asc':
        return new Date(a.created_at) - new Date(b.created_at);
      default:
        return 0;
    }
  });

  return (
    <div className="space-y-4">
      {/* Header com busca, ordenação e Adicionar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-sm text-gray-600">
          {filteredResellers.length} de {resellers.length} revendedores
        </p>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          {/* Campo de busca por nome ou KAKO ID */}
          <div className="flex items-center gap-2 flex-1 sm:flex-initial">
            <Input
              placeholder="🔍 Buscar por nome ou KAKO ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-[260px]"
              data-testid="reseller-search-input"
            />
            {searchTerm && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSearchTerm('')}
                className="h-9 px-2 text-xs"
                title="Limpar"
              >
                ✕
              </Button>
            )}
          </div>
          
          {/* Select de Ordenação */}
          <div className="flex items-center gap-2 flex-1 sm:flex-initial">
            <Label className="text-sm whitespace-nowrap">Ordenar por:</Label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nickname_asc">Nome (A-Z)</SelectItem>
                <SelectItem value="nickname_desc">Nome (Z-A)</SelectItem>
                <SelectItem value="total_sales_desc">Vendas Totais (Maior)</SelectItem>
                <SelectItem value="total_sales_asc">Vendas Totais (Menor)</SelectItem>
                <SelectItem value="week_sales_desc">Vendas Semana (Maior)</SelectItem>
                <SelectItem value="week_sales_asc">Vendas Semana (Menor)</SelectItem>
                <SelectItem value="last_week_sales_desc">Semana Anterior (Maior)</SelectItem>
                <SelectItem value="last_week_sales_asc">Semana Anterior (Menor)</SelectItem>
                <SelectItem value="created_desc">Mais Recente</SelectItem>
                <SelectItem value="created_asc">Mais Antigo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button onClick={() => onEdit(null)} className="bg-purple-600 whitespace-nowrap">
            <Plus className="mr-2 h-4 w-4" />
            Novo
          </Button>
        </div>
      </div>

      {/* Sub-abas HOST / AGENTE */}
      <div className="overflow-x-auto">
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <TabsList className="inline-flex w-auto min-w-full">
            <TabsTrigger value="all">Todos ({resellers.length})</TabsTrigger>
            <TabsTrigger value="HOST">HOST ({resellers.filter(r => r.type === 'HOST').length})</TabsTrigger>
            <TabsTrigger value="AGENTE">AGENTE ({resellers.filter(r => r.type === 'AGENTE').length})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Tabela de Revendedores */}
      {sortedResellers.length === 0 ? (
        <p className="text-center text-gray-500 py-8">Nenhum revendedor cadastrado</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="text-left p-3 font-semibold">Avatar</th>
                    <th className="text-left p-3 font-semibold">Nickname</th>
                    <th className="text-left p-3 font-semibold">KAKO ID</th>
                    <th className="text-left p-3 font-semibold">WhatsApp</th>
                    <th className="text-center p-3 font-semibold">Tipo</th>
                    <th className="text-center p-3 font-semibold">Status</th>
                    <th className="text-left p-3 font-semibold">Senha</th>
                    <th className="text-center p-3 font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedResellers.map((r) => (
                    <tr key={r.id} className="border-b hover:bg-gray-50">
                      {/* Avatar */}
                      <td className="p-3">
                        {r.kako_avatar ? (
                          <img src={r.kako_avatar} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">👤</div>
                        )}
                      </td>
                      
                      {/* Nickname */}
                      <td className="p-3 font-medium">{r.nickname || 'Revendedor'}</td>
                      
                      {/* KAKO ID - Clicável */}
                      <td 
                        className="p-3 font-mono text-xs cursor-pointer hover:bg-purple-50 hover:text-purple-600"
                        onClick={() => {
                          navigator.clipboard.writeText(r.kako_id);
                          toast.success('KAKO ID copiado!', { duration: 1500 });
                        }}
                        title="Clique para copiar"
                      >
                        {r.kako_id}
                      </td>
                      
                      {/* WhatsApp - Clicável */}
                      <td 
                        className="p-3 text-xs cursor-pointer hover:bg-green-50 hover:text-green-600 font-mono"
                        onClick={() => {
                          if (r.whatsapp) {
                            navigator.clipboard.writeText(r.whatsapp);
                            toast.success('WhatsApp copiado!', { duration: 1500 });
                          }
                        }}
                        title="Clique para copiar WhatsApp"
                      >
                        {r.whatsapp || '-'}
                      </td>
                      
                      {/* Tipo */}
                      <td className="p-3 text-center">
                        {r.type ? (
                          <Badge variant={r.type === 'HOST' ? 'default' : 'secondary'}>
                            {r.type}
                          </Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      
                      {/* Status */}
                      <td className="p-3 text-center">
                        <Badge variant={r.active ? 'default' : 'secondary'}>
                          {r.active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </td>
                      
                      {/* Senha */}
                      <td 
                        className="p-3 text-xs font-mono cursor-pointer hover:bg-purple-50 hover:text-purple-600"
                        onClick={() => {
                          if (r.plain_password) {
                            navigator.clipboard.writeText(r.plain_password);
                            toast.success('Senha copiada!', { duration: 1500 });
                          }
                        }}
                        title={r.plain_password ? "Clique para copiar senha" : "Senha não disponível - edite para redefinir"}
                      >
                        {r.plain_password || '-'}
                      </td>
                      
                      {/* Ações */}
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => setReportReseller(r)}
                            className="h-7 text-xs bg-green-600 hover:bg-green-700"
                            data-testid={`report-btn-${r.id}`}
                          >
                            📊 Relatório
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => onEdit(r)} className="h-7 text-xs">
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => onDelete(r.id)} className="h-7 text-xs">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de Relatório Mensal de Vendas */}
      <Dialog open={reportReseller !== null} onOpenChange={() => setReportReseller(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">📊</span>
              <div>
                <div>Relatório Mensal de Vendas</div>
                <div className="text-sm font-normal text-gray-600">
                  {reportReseller?.nickname} (KAKO {reportReseller?.kako_id})
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label className="text-sm">Selecione o mês</Label>
              <Input
                type="month"
                value={reportMonth}
                onChange={(e) => setReportMonth(e.target.value)}
                data-testid="report-month-input"
              />
              <p className="text-xs text-gray-500">
                O relatório será baixado em <strong>Excel (.xlsx)</strong> com cabeçalho azul e títulos em negrito.
                Apenas vendas pagas e creditadas são incluídas.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setReportReseller(null)} disabled={generatingReport}>
                Cancelar
              </Button>
              <Button
                onClick={generateReport}
                disabled={generatingReport || !reportMonth}
                className="bg-green-600 hover:bg-green-700"
                data-testid="generate-report-btn"
              >
                {generatingReport ? 'Gerando...' : '📥 Baixar Excel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ResellersManagement = () => {
  const [resellers, setResellers] = useState([]);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [applications, setApplications] = useState([]); // Novo
  const [tasks, setTasks] = useState([]);
  const [editingPositionId, setEditingPositionId] = useState(null);
  const [positionInput, setPositionInput] = useState('');
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [claimsHistory, setClaimsHistory] = useState([]);
  const [claimsFilter, setClaimsFilter] = useState({ kako_id: '', start_date: '', end_date: '' });
  const [claimsSortBy, setClaimsSortBy] = useState('claimed_desc');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({ 
    kako_id: '', 
    password: '', 
    active: true,
    type: null,
    followers_count: '',
    agency: '',
    agency_id: '',
    host_count: ''
  });
  const [taskForm, setTaskForm] = useState({ title: '', description: '', target_sales_coins: '', reward_coins: '', active: true });

  useEffect(() => {
    loadResellers();
    loadApplications(); // Novo
    loadTasks();
    loadClaimsHistory();
    loadConfig();
  }, []);

  const loadClaimsHistory = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/reseller-tasks/claims-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaimsHistory(response.data);
      setSelectedClaims([]);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const loadConfig = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/config/`);
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config:', error);
    }
  };

  const toggleResellerRanking = async (active) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      await axios.put(`${BACKEND_URL}/api/config/?reseller_ranking_active=${active}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Ranking ${active ? 'ativado' : 'desativado'}!`);
      loadConfig();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const toggleResellerTasks = async (active) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      await axios.put(`${BACKEND_URL}/api/config/?reseller_tasks_active=${active}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Tarefas de Afiliados ${active ? 'ativadas' : 'desativadas'}!`);
      loadConfig();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };


  const handleDeleteSelectedClaims = async () => {
    if (selectedClaims.length === 0) {
      toast.error('Selecione pelo menos um resgate');
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/reseller-tasks/claims/delete-bulk`, selectedClaims, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${selectedClaims.length} resgate(s) excluído(s)!`);
      setShowDeleteConfirm(false);
      loadClaimsHistory();
    } catch (error) {
      toast.error('Erro ao excluir resgates');
      setShowDeleteConfirm(false);
    }
  };

  const loadApplications = async () => {
    try {
      // Carrega TODAS (pending E rejected) - não mais apenas pending
      const [pendingRes, rejectedRes] = await Promise.all([
        applicationsAPI.list('pending'),
        applicationsAPI.list('rejected')
      ]);
      
      // Combina as duas listas
      setApplications([...pendingRes.data, ...rejectedRes.data]);
    } catch (error) {
      console.error('Erro ao carregar inscrições:', error);
    }
  };

  const handleApproveApplication = async (appId) => {
    try {
      await applicationsAPI.approve(appId);
      toast.success('Solicitação aprovada! Afiliado criado com sucesso.');
      loadApplications();
      loadResellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar');
    }
  };

  const handleRejectApplication = async (appId) => {
    const rejectionMessage = `Olá! 💛

Agradecemos sinceramente o seu interesse em fazer parte da FOX SELLER.
Analisamos sua solicitação com muito cuidado, porém, no momento, ela não atende aos requisitos mínimos necessários para participação no programa.

Nosso objetivo é manter um padrão de qualidade e consistência que beneficie todos os afiliados, garantindo credibilidade, segurança e crescimento sólido dentro do KAKO LIVE.

Mas não desanime! ✨
Continue se dedicando, fortalecendo seu perfil e ampliando sua presença na plataforma — estaremos de portas abertas para uma nova análise futuramente.

Com carinho e respeito,
Equipe FOX SELLER 🦊`;

    try {
      await applicationsAPI.reject(appId, rejectionMessage);
      // Removido toast
      loadApplications();
    } catch (error) {
      toast.error('Erro ao rejeitar');
    }
  };

  const toggleClaimSelection = (claimId) => {
    setSelectedClaims(prev => 
      prev.includes(claimId) 
        ? prev.filter(id => id !== claimId)
        : [...prev, claimId]
    );
  };

  const toggleSelectAll = (weekClaims) => {
    const weekIds = weekClaims.map(c => c.id);
    const allSelected = weekIds.every(id => selectedClaims.includes(id));
    
    if (allSelected) {
      setSelectedClaims(prev => prev.filter(id => !weekIds.includes(id)));
    } else {
      setSelectedClaims(prev => [...new Set([...prev, ...weekIds])]);
    }
  };

  const loadTasks = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/reseller-tasks/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Erro ao carregar tarefas:', error);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const data = {
        title: taskForm.title,
        description: taskForm.description,
        target_sales_coins: parseInt(taskForm.target_sales_coins),
        reward_coins: parseInt(taskForm.reward_coins),
        active: taskForm.active
      };

      if (editingTask) {
        await axios.put(`${BACKEND_URL}/api/reseller-tasks/${editingTask.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tarefa atualizada!');
      } else {
        await axios.post(`${BACKEND_URL}/api/reseller-tasks/`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tarefa criada!');
      }
      
      setTaskDialogOpen(false);
      setEditingTask(null);
      setTaskForm({ title: '', description: '', target_sales_coins: '', reward_coins: '', active: true });
      loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar tarefa');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Remover esta tarefa?')) return;
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      await axios.delete(`${BACKEND_URL}/api/reseller-tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tarefa removida!');
      loadTasks();
    } catch (error) {
      toast.error('Erro ao remover tarefa');
    }
  };

  const handleToggleTaskActive = async (taskId, newActiveStatus) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      // Busca tarefa atual
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;
      
      // Atualiza apenas o campo active
      await axios.put(
        `${BACKEND_URL}/api/reseller-tasks/${taskId}`,
        {
          title: task.title,
          description: task.description,
          target_sales_coins: task.target_sales_coins,
          reward_coins: task.reward_coins,
          active: newActiveStatus
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Tarefa ${newActiveStatus ? 'ativada' : 'desativada'}!`);
      loadTasks();
    } catch (error) {
      toast.error('Erro ao atualizar tarefa');
    }
  };

  const persistTaskOrder = async (newOrder) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${BACKEND_URL}/api/reseller-tasks/reorder`,
        newOrder.map(t => t.id),
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      toast.error('Erro ao reordenar tarefa');
      loadTasks();
    }
  };

  const handleMoveTask = async (taskId, direction) => {
    // direction: -1 para cima, +1 para baixo
    const idx = tasks.findIndex(t => t.id === taskId);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= tasks.length) return;

    const reordered = arrayMove(tasks, idx, newIdx);
    setTasks(reordered);
    await persistTaskOrder(reordered);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = tasks.findIndex(t => t.id === active.id);
    const newIdx = tasks.findIndex(t => t.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(tasks, oldIdx, newIdx);
    setTasks(reordered);
    await persistTaskOrder(reordered);
  };

  const handleCommitPosition = async (taskId) => {
    const targetPos = parseInt(positionInput, 10);
    setEditingPositionId(null);
    if (Number.isNaN(targetPos) || targetPos < 1 || targetPos > tasks.length) {
      toast.error(`Digite um número entre 1 e ${tasks.length}`);
      return;
    }
    const oldIdx = tasks.findIndex(t => t.id === taskId);
    const newIdx = targetPos - 1;
    if (oldIdx < 0 || oldIdx === newIdx) return;
    const reordered = arrayMove(tasks, oldIdx, newIdx);
    setTasks(reordered);
    await persistTaskOrder(reordered);
    toast.success(`Tarefa movida para posição #${targetPos}`);
  };


  const loadResellers = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/reseller/?skip=0&limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResellers(response.data);
      console.log('Revendedores carregados:', response.data.length);
    } catch (error) {
      toast.error('Erro ao carregar revendedores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      // Validações adicionais no frontend
      if (!editing && (!formData.kako_id || formData.kako_id.length < 1)) {
        toast.error('KAKO ID é obrigatório');
        return;
      }
      
      if (!editing && (!formData.password || formData.password.length < 6)) {
        toast.error('Senha deve ter no mínimo 6 caracteres');
        return;
      }
      
      const dataToSend = editing 
        ? { 
            password: formData.password || undefined, 
            nickname: formData.nickname, 
            active: formData.active,
            can_view_customers: formData.can_view_customers,  // NOVO
            type: formData.type || undefined,
            followers_count: formData.followers_count ? parseInt(formData.followers_count) : undefined,
            agency: formData.agency || undefined,
            agency_id: formData.agency_id || undefined,
            host_count: formData.host_count ? parseInt(formData.host_count) : undefined
          }
        : { 
            kako_id: formData.kako_id, 
            password: formData.password,
            type: formData.type || undefined,
            followers_count: formData.followers_count ? parseInt(formData.followers_count) : undefined,
            agency: formData.agency || undefined,
            agency_id: formData.agency_id || undefined,
            host_count: formData.host_count ? parseInt(formData.host_count) : undefined
          };
      
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/reseller/${editing.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Revendedor atualizado!');
      } else {
        const response = await axios.post(`${BACKEND_URL}/api/reseller/`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Revendedor criado:', response.data);
        toast.success('Revendedor criado com sucesso!');
      }
      setDialogOpen(false);
      setFormData({ kako_id: '', password: '', active: true, type: null, followers_count: '', agency: '', agency_id: '', host_count: '' });
      loadResellers();
    } catch (error) {
      console.error('Erro ao salvar revendedor:', error);
      console.error('Detalhes do erro:', error.response?.data);
      const errorMsg = error.response?.data?.detail || 
                       error.response?.data?.message ||
                       (error.response?.status === 422 ? 'Dados inválidos. Verifique o KAKO ID (máx 8 dígitos, apenas números)' : 'Erro ao salvar');
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este revendedor?')) return;
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      await axios.delete(`${BACKEND_URL}/api/reseller/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Revendedor removido!');
      loadResellers();
    } catch (error) {
      console.error('Erro ao remover:', error);
      toast.error(error.response?.data?.detail || 'Erro ao remover');
    }
  };

  if (loading) return <div className="flex justify-center p-8"><FoxSpinnerInline size="lg" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h2 className="text-2xl font-bold">Gerenciar Afiliados</h2>
          <p className="text-gray-600">Revendedores e suas tarefas</p>
        </div>
        <Button
          variant="outline"
          className="bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
          onClick={() => setShowVisibilityModal(true)}
          data-testid="open-visibility-modal-btn"
        >
          👁️ Visibilidade de Clientes
        </Button>
      </div>

      <VisibilityManagerModal
        open={showVisibilityModal}
        onClose={() => setShowVisibilityModal(false)}
        resellers={resellers}
        onUpdated={() => loadResellers()}
      />

      <Tabs defaultValue="resellers" className="w-full">
        <div className="overflow-x-auto">
          <TabsList className="inline-flex w-auto min-w-full">
            <TabsTrigger value="applications">
              <FileText className="w-4 h-4 mr-2" />
              Inscrições {applications.filter(a => a.status === 'pending').length > 0 && <Badge className="ml-1 bg-red-500">{applications.filter(a => a.status === 'pending').length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="resellers">
              <Users className="w-4 h-4 mr-2" />
              Revendedores
            </TabsTrigger>
            <TabsTrigger value="tasks">
              <Target className="w-4 h-4 mr-2" />
              Tarefas
            </TabsTrigger>
            <TabsTrigger value="claims">
              <Gift className="w-4 h-4 mr-2" />
              Resgates
            </TabsTrigger>
            <TabsTrigger value="ranking">
              <Trophy className="w-4 h-4 mr-2" />
              Ranking
            </TabsTrigger>
            <TabsTrigger value="winners">
              <Crown className="w-4 h-4 mr-2" />
              Ganhadores
            </TabsTrigger>

            <TabsTrigger value="sales-history">
              <FileText className="w-4 h-4 mr-2" />
              Histórico Vendas
            </TabsTrigger>

            <TabsTrigger value="avisos">
              <Bell className="w-4 h-4 mr-2" />
              Avisos
            </TabsTrigger>

          </TabsList>
        </div>

        {/* Aba Inscrições - NOVA */}
        <TabsContent value="applications" className="mt-6">
          <ApplicationsTabs applications={applications} onApprove={handleApproveApplication} onReject={handleRejectApplication} onReload={loadApplications} />
        </TabsContent>

        <TabsContent value="resellers" className="mt-6">
          <ResellersTabs 
            resellers={resellers} 
            onEdit={(r) => { 
              setEditing(r); 
              if (r) {
                setFormData({ 
                  password: '', 
                  nickname: r.nickname, 
                  active: r.active,
                  can_view_customers: r.can_view_customers || false,  // NOVO
                  type: r.type, 
                  followers_count: r.followers_count || '', 
                  agency: r.agency || '', 
                  agency_id: r.agency_id || '', 
                  host_count: r.host_count || '' 
                });
              } else {
                setFormData({ 
                  kako_id: '', 
                  password: '', 
                  active: true, 
                  type: null, 
                  followers_count: '', 
                  agency: '', 
                  agency_id: '', 
                  host_count: '' 
                });
              }
              setDialogOpen(true); 
            }} 
            onDelete={handleDelete} 
          />
        </TabsContent>

        <TabsContent value="tasks" className="mt-6">
          {/* Switch Global de Tarefas de Afiliados */}
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">Sistema de Tarefas de Afiliados</p>
                  <p className="text-xs text-gray-600">Quando desativado, tarefas não aparecem para afiliados</p>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-sm">Status:</Label>
                  <Switch
                    checked={config?.reseller_tasks_active || false}
                    onCheckedChange={toggleResellerTasks}
                  />
                  <Badge variant={config?.reseller_tasks_active ? 'default' : 'secondary'}>
                    {config?.reseller_tasks_active ? 'ATIVO' : 'INATIVO'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              {tasks.length} tarefas configuradas
              <span className="ml-2 text-xs text-gray-400">• Arraste para reordenar ou clique no número para mover rápido</span>
            </p>
            <Button onClick={() => setTaskDialogOpen(true)} className="bg-blue-600">
              <Plus className="mr-2 h-4 w-4" />
              Nova Tarefa
            </Button>
          </div>

          <DndContext
            sensors={dndSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
              <div className="grid gap-4 md:grid-cols-2">
                {tasks.map((task, idx) => (
                  <SortableTaskCard
                    key={task.id}
                    task={task}
                    idx={idx}
                    total={tasks.length}
                    isEditingPosition={editingPositionId === task.id}
                    positionInput={positionInput}
                    onStartEditPosition={() => {
                      setEditingPositionId(task.id);
                      setPositionInput(String(idx + 1));
                    }}
                    onChangePositionInput={setPositionInput}
                    onCommitPosition={() => handleCommitPosition(task.id)}
                    onCancelEditPosition={() => setEditingPositionId(null)}
                    onMove={handleMoveTask}
                    onToggleActive={handleToggleTaskActive}
                    onDelete={handleDeleteTask}
                    onEdit={() => {
                      setEditingTask(task);
                      setTaskForm({
                        title: task.title,
                        description: task.description,
                        target_sales_coins: task.target_sales_coins.toString(),
                        reward_coins: task.reward_coins.toString(),
                        active: task.active
                      });
                      setTaskDialogOpen(true);
                    }}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </TabsContent>


        <TabsContent value="claims" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-green-600" />
                Histórico de Resgates de Revendedores
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filtros e Ordenação — modelo Tarefas VIP */}
              <div className="grid gap-4 md:grid-cols-5">
                <div className="space-y-2">
                  <Label className="text-sm">KAKO ID</Label>
                  <Input
                    placeholder="Filtrar por revendedor"
                    value={claimsFilter.kako_id}
                    onChange={(e) => setClaimsFilter({ ...claimsFilter, kako_id: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Data Início</Label>
                  <Input
                    type="date"
                    value={claimsFilter.start_date}
                    onChange={(e) => setClaimsFilter({ ...claimsFilter, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Data Fim</Label>
                  <Input
                    type="date"
                    value={claimsFilter.end_date}
                    onChange={(e) => setClaimsFilter({ ...claimsFilter, end_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Ordenar por</Label>
                  <select
                    value={claimsSortBy}
                    onChange={(e) => setClaimsSortBy(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="claimed_desc">Resgate (Recente)</option>
                    <option value="claimed_asc">Resgate (Antigo)</option>
                    <option value="coins_desc">Moedas (Maior)</option>
                    <option value="coins_asc">Moedas (Menor)</option>
                    <option value="kako_id_asc">KAKO ID (A-Z)</option>
                    <option value="kako_id_desc">KAKO ID (Z-A)</option>
                    <option value="task_asc">Tarefa (A-Z)</option>
                    <option value="task_desc">Tarefa (Z-A)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">&nbsp;</Label>
                  <Button onClick={() => { setClaimsFilter({ kako_id: '', start_date: '', end_date: '' }); }} variant="outline" className="w-full">
                    Limpar Filtros
                  </Button>
                </div>
              </div>

              {/* Botão de Delete Selecionados */}
              {selectedClaims.length > 0 && (
                <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded mb-3">
                  <span className="text-sm font-medium">
                    {selectedClaims.length} resgate(s) selecionado(s)
                  </span>
                  <Button
                    onClick={handleDeleteSelectedClaims}
                    variant="destructive"
                    size="sm"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Excluir Selecionados
                  </Button>
                </div>
              )}

              {/* Lista Agrupada por Data — modelo Tarefas VIP */}
              <div className="space-y-3">
                {(() => {
                  // Aplica filtros + ordenação client-side
                  let filtered = [...claimsHistory];
                  if (claimsFilter.kako_id.trim()) {
                    const q = claimsFilter.kako_id.trim().toLowerCase();
                    filtered = filtered.filter(c =>
                      (c.reseller_kako_id || '').toLowerCase().includes(q) ||
                      (c.reseller_nickname || '').toLowerCase().includes(q)
                    );
                  }
                  const getClaimDate = (c) => (c.reward_claimed_at || '').slice(0, 10);
                  if (claimsFilter.start_date) {
                    filtered = filtered.filter(c => getClaimDate(c) >= claimsFilter.start_date);
                  }
                  if (claimsFilter.end_date) {
                    filtered = filtered.filter(c => getClaimDate(c) <= claimsFilter.end_date);
                  }
                  filtered.sort((a, b) => {
                    switch (claimsSortBy) {
                      case 'claimed_desc': return new Date(b.reward_claimed_at || 0) - new Date(a.reward_claimed_at || 0);
                      case 'claimed_asc':  return new Date(a.reward_claimed_at || 0) - new Date(b.reward_claimed_at || 0);
                      case 'coins_desc':   return (b.reward_coins || 0) - (a.reward_coins || 0);
                      case 'coins_asc':    return (a.reward_coins || 0) - (b.reward_coins || 0);
                      case 'kako_id_asc':  return (a.reseller_kako_id || '').localeCompare(b.reseller_kako_id || '');
                      case 'kako_id_desc': return (b.reseller_kako_id || '').localeCompare(a.reseller_kako_id || '');
                      case 'task_asc':     return (a.task_title || '').localeCompare(b.task_title || '');
                      case 'task_desc':    return (b.task_title || '').localeCompare(a.task_title || '');
                      default: return 0;
                    }
                  });

                  if (filtered.length === 0) {
                    return <p className="text-center text-gray-500 py-8">Nenhum resgate encontrado</p>;
                  }

                  // Agrupa por data de resgate
                  const groups = filtered.reduce((acc, claim) => {
                    const date = getClaimDate(claim) || 'sem-data';
                    if (!acc[date]) acc[date] = [];
                    acc[date].push(claim);
                    return acc;
                  }, {});

                  return Object.entries(groups).map(([date, dayClaims]) => (
                    <div key={date} className="border rounded-lg p-3 bg-green-50 mb-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={dayClaims.every(c => selectedClaims.includes(c.id))}
                            onCheckedChange={() => toggleSelectAll(dayClaims)}
                          />
                          <h4 className="font-bold text-sm text-green-700">
                            📅 {date !== 'sem-data' ? date.split('-').reverse().join('/') : 'Sem data'}
                          </h4>
                        </div>
                        <Badge className="bg-green-600 text-white">
                          {dayClaims.length} resgate(s) • Total: {dayClaims.reduce((s, c) => s + (c.reward_coins || 0), 0).toLocaleString()} 🪙
                        </Badge>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="p-2 w-10">
                                <Checkbox
                                  checked={dayClaims.every(c => selectedClaims.includes(c.id))}
                                  onCheckedChange={() => toggleSelectAll(dayClaims)}
                                />
                              </th>
                              <th className="text-left p-2">Tarefa</th>
                              <th className="text-left p-2">Revendedor</th>
                              <th className="text-left p-2">Prêmio</th>
                              <th className="text-left p-2">Resgatado em</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dayClaims.map((claim) => (
                              <tr key={claim.id} className="border-b hover:bg-white">
                                <td className="p-2">
                                  <Checkbox
                                    checked={selectedClaims.includes(claim.id)}
                                    onCheckedChange={() => toggleClaimSelection(claim.id)}
                                  />
                                </td>
                                <td className="p-2 text-xs">{claim.task_title || '-'}</td>
                                <td className="p-2">
                                  <div className="text-xs">
                                    <div className="font-medium">{claim.reseller_nickname || claim.reseller_kako_id}</div>
                                    <div className="text-gray-500">KAKO ID: {claim.reseller_kako_id}</div>
                                  </div>
                                </td>
                                <td className="p-2">
                                  <Badge className="bg-green-600 text-white text-xs">
                                    +{(claim.reward_coins || 0).toLocaleString()} 🎁
                                  </Badge>
                                </td>
                                <td className="p-2 text-xs">
                                  {claim.reward_claimed_at ? new Date(claim.reward_claimed_at).toLocaleString('pt-BR') : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Histórico de Vendas (imutável) */}
        <TabsContent value="sales-history" className="mt-6">
          <ResellerSalesHistory />
        </TabsContent>

        {/* Aba Avisos */}
        <TabsContent value="avisos" className="mt-6">
          <ResellerNoticesManagement />
        </TabsContent>

        {/* Aba Ranking */}
        <TabsContent value="ranking" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  <CardTitle>Ranking de Afiliados</CardTitle>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-sm">Ranking Público:</Label>
                  <Switch
                    checked={config?.reseller_ranking_active || false}
                    onCheckedChange={toggleResellerRanking}
                  />
                  <Badge variant={config?.reseller_ranking_active ? 'default' : 'secondary'}>
                    {config?.reseller_ranking_active ? 'ATIVO' : 'INATIVO'}
                  </Badge>
                </div>
              </div>
              {config?.reseller_ranking_active && (
                <p className="text-sm text-gray-600 mt-2">
                  🌐 Página pública disponível em: <strong>/ranking-afiliados</strong>
                </p>
              )}
            </CardHeader>
            <CardContent>
              <RankingTabs />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Ganhadores - NOVA */}
        <TabsContent value="winners" className="mt-6">
          <WinnersHistory />
        </TabsContent>

      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Revendedor' : 'Novo Revendedor'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Altere os dados do revendedor' : 'Preencha os dados para criar um novo revendedor'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editing && (
              <div>
                <Label>KAKO ID do Revendedor</Label>
                <Input 
                  data-testid="reseller-kako-id-input"
                  type="text" 
                  inputMode="numeric"
                  maxLength={8} 
                  placeholder="Ex: 10765749 (máx 8 dígitos)"
                  value={formData.kako_id} 
                  onChange={(e) => setFormData({...formData, kako_id: e.target.value.replace(/\D/g, '').slice(0, 8)})} 
                  required 
                />
                <p className="text-xs text-gray-500 mt-1">
                  ✓ Apenas números | Máximo 8 dígitos | Sistema buscará avatar e nickname automaticamente
                </p>
              </div>
            )}
            
            <div>
              <Label>Senha de Acesso</Label>
              <Input 
                type="password" 
                placeholder={editing ? "Deixe em branco para manter a senha atual" : "Defina uma senha"}
                value={formData.password} 
                onChange={(e) => setFormData({...formData, password: e.target.value})} 
                required={!editing} 
              />
              <p className="text-xs text-gray-500 mt-1">
                Mínimo 6 caracteres
              </p>
            </div>
            
            {editing && (
              <>
                {/* Tipo de Afiliado */}
                <div>
                  <Label>Tipo de Afiliado</Label>
                  <select 
                    className="w-full border rounded px-3 py-2"
                    value={formData.type || ''}
                    onChange={(e) => setFormData({...formData, type: e.target.value || null})}
                  >
                    <option value="">Nenhum</option>
                    <option value="HOST">HOST</option>
                    <option value="AGENTE">AGENTE</option>
                  </select>
                </div>

                {/* Campos HOST */}
                {formData.type === 'HOST' && (
                  <>
                    <div>
                      <Label>Número de Seguidores</Label>
                      <Input
                        type="number"
                        placeholder="Ex: 5000"
                        value={formData.followers_count}
                        onChange={(e) => setFormData({...formData, followers_count: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Agência</Label>
                      <Input
                        type="text"
                        placeholder="Nome da agência"
                        value={formData.agency}
                        onChange={(e) => setFormData({...formData, agency: e.target.value})}
                      />
                    </div>
                  </>
                )}

                {/* Campos AGENTE */}
                {formData.type === 'AGENTE' && (
                  <>
                    <div>
                      <Label>ID de Agência</Label>
                      <Input
                        type="text"
                        placeholder="ID da agência"
                        value={formData.agency_id}
                        onChange={(e) => setFormData({...formData, agency_id: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Número de Hosts</Label>
                      <Input
                        type="number"
                        placeholder="Ex: 10"
                        value={formData.host_count}
                        onChange={(e) => setFormData({...formData, host_count: e.target.value})}
                      />
                    </div>
                  </>
                )}

                <div className="flex items-center gap-2">
                  <Switch checked={formData.active} onCheckedChange={(c) => setFormData({...formData, active: c})} />
                  <Label>Revendedor Ativo</Label>
                </div>
              </>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-purple-600">{editing ? 'Salvar' : 'Criar Revendedor'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Criar Tarefa */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Editar Tarefa' : 'Nova Tarefa de Revendedor'}</DialogTitle>
            <DialogDescription>
              {editingTask ? 'Altere os dados da tarefa' : 'Crie uma nova meta para os revendedores'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div>
              <Label>Título</Label>
              <Input placeholder="Ex: Venda 500K moedas" value={taskForm.title} onChange={(e) => setTaskForm({...taskForm, title: e.target.value})} required />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input placeholder="Detalhes da tarefa" value={taskForm.description} onChange={(e) => setTaskForm({...taskForm, description: e.target.value})} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Meta (Moedas)</Label>
                <Input type="number" placeholder="500000" value={taskForm.target_sales_coins} onChange={(e) => setTaskForm({...taskForm, target_sales_coins: e.target.value})} required />
              </div>
              <div>
                <Label>Bônus (Moedas)</Label>
                <Input type="number" placeholder="50000" value={taskForm.reward_coins} onChange={(e) => setTaskForm({...taskForm, reward_coins: e.target.value})} required />
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded">
              <Switch 
                checked={taskForm.active} 
                onCheckedChange={(checked) => setTaskForm({...taskForm, active: checked})}
              />
              <Label>Tarefa Ativa</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-blue-600">{editingTask ? 'Salvar Alterações' : 'Criar Tarefa'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Delete */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja excluir <strong>{selectedClaims.length} resgate(s)</strong> selecionado(s)?
          </p>
          <p className="text-xs text-red-600 mt-2">
            ⚠️ Esta ação irá reverter os resgates de revendedores.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Confirmar Exclusão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResellersManagement;
